
import Vue from "vue";
import { formatDateWithTimestamp, formatDatepickerDate } from "@/shared/utils/common_utils";
import TitledSubsection from "@/shared/components/TitledSubsection.vue";
import AppError from "@/shared/components/AppError.vue";
import { Line as LineChartGenerator } from "vue-chartjs/legacy"; // Important to rename this component to something other than "Line"
import zoomPlugin from "chartjs-plugin-zoom";
import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    LineElement,
    LinearScale,
    TimeScale,
    CategoryScale,
    PointElement,
    ChartData,
    ChartDataset,
    Scale,
    ScriptableContext,
    TooltipXAlignment,
    TooltipYAlignment,
    Tick,
} from "chart.js";
import "chartjs-adapter-date-fns";
import ScorecardTemplate from "@/features/Scores/ScorecardTemplate";
import TimelinePoint, { TimelinePointType } from "@/features/Timeline/TimelinePoint";
import TimelineSeries from "@/features/Timeline/TimelineSeries";
import { UIIncidentSeverity } from "@/features/Incidents/Incident";
import TimelinePointDetails from "@/features/Timeline/TimelinePointDetails";
ChartJS.register(Title, Tooltip, Legend, LineElement, LinearScale, TimeScale, CategoryScale, PointElement);
ChartJS.register(zoomPlugin);

Tooltip.positioners.custom = function (items, position) {
    // Similar to built-in default "average" position for snappiness
    // But we will move the bubble away from the edge afterwards
    // Original: https://github.com/chartjs/Chart.js/blob/83ba8fd77689645d0f915a6c8b4881b882be5276/src/plugins/plugin.tooltip.js#L21
    if (!items.length) {
        return false;
    }

    let i, len;
    let x = 0;
    let y = 0;
    let count = 0;

    for (i = 0, len = items.length; i < len; ++i) {
        const el = items[i].element;
        if (el && el.hasValue()) {
            const pos = el.tooltipPosition();
            x += pos.x;
            y += pos.y;
            ++count;
        }
    }

    const averagePosition = {
        x: x / count,
        y: y / count,
    };

    var xal: TooltipXAlignment = "center";
    var yal: TooltipYAlignment = "top";
    // By default, show tooltip under-center
    // If mouse is near the left, right, or bottom edges of the canvas,
    // Move to tooltip towards the center of the canvas
    if (position.x > this.chart.width * 0.7) {
        xal = "right";
    } else if (position.x < this.chart.width * 0.3) {
        xal = "left";
    }
    if (position.y > this.chart.height * 0.7) {
        yal = "bottom";
    }
    return {
        x: averagePosition.x,
        y: averagePosition.y,
        xAlign: xal,
        yAlign: yal,
    };
};

const START_TIME_FOR_YEAR_TIME_SCALE = 1000 * 60 * 60 * 24 * 365 * 3; // 3 years. Timespan where we switch to a year timescale on the X axis.

export default Vue.extend({
    name: "TimelineGraph",
    components: {
        LineChartGenerator,
        TitledSubsection,
        AppError,
    },
    props: {
        supplierID: {
            type: String,
            required: true,
        },
        chartId: {
            type: String,
            default: "supplier-timeline-chart",
        },
        height: {
            type: Number,
            default: 300,
        },
    },
    data: () => ({
        timelineSeries: [] as TimelineSeries[],
        graphStartDateMenu: false,
        graphEndDateMenu: false,
        graphStartDateString: "",
        graphEndDateString: "",
        scorecardTemplate: null as ScorecardTemplate | null,
        currentTab: null as number | null,
        tabList: [] as string[],
        showScorecards: true,
        showIncidents: false,
        showSupplierEvents: false,
        showSections: {} as { [key: string]: boolean },
        showQuestions: {} as { [key: string]: boolean },
        loading: false,
        loadingScorecardTemplate: false,
        errorMessage: "",
        showError: false,
        pointRadius: 7,
        pointBorderWidth: 2,
    }),
    async mounted() {
        if (this.$route.query.beforeDate && this.$route.query.afterDate) {
            this.graphEndDateString = this.$route.query.beforeDate as string;
            this.graphStartDateString = this.$route.query.afterDate as string;
        }

        await this.getScorecardTemplate();
        this.getTimelineData();
    },
    watch: {
        supplierID() {
            this.getTimelineData();
        },
        graphEndDateString(newValue: string) {
            // For some reason, the input updates the string twice: once with just the date, and once with date and time
            // To avoid emitting duplicate events, only emit if the date has a UTC time (last character is Z)
            if (newValue[newValue.length - 1] == "Z") {
                this.$emit("update-date-range", this.graphStartDateString, newValue);
            }
        },
        graphStartDateString(newValue: string) {
            // For some reason, the input updates the string twice: once with just the date, and once with date and UTC time
            // To avoid emitting duplicate events, only emit if the date has a UTC time (last character is Z)
            if (newValue[newValue.length - 1] == "Z") {
                this.$emit("update-date-range", newValue, this.graphEndDateString);
            }
        },
        afterDate(newValue: string) {
            if (newValue) {
                this.graphStartDateString = newValue;
                this.getTimelineData();
            }
        },
        beforeDate(newValue: string) {
            if (newValue) {
                this.graphEndDateString = newValue;
                this.getTimelineData();
            }
        },
    },
    methods: {
        chartClicked: function (event: PointerEvent) {
            const chartRef = this.$refs.chart!;

            // Accessing the chart instance requires calling a function that Typescript believes will not exist on this ref. https://vue-chartjs.org/guide/#access-to-chart-instance
            // The documentation implies "getCurrentChart" is a property but it's actually a function.
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore Disabling type checking on this line in the absence of a type with getCurrentChart() defined to cast the ref to.
            const currentChart = chartRef.getCurrentChart() as ChartJS;

            // Get the set of chart elements intersecting the click.
            const activePoints = currentChart.getElementsAtEventForMode(event, "nearest", { intersect: true }, true);

            if (activePoints.length) {
                // If a point was directly clicked, tell our parent which point
                const firstClickPoint = activePoints[0];
                const timelineDataPoint = currentChart.data.datasets[firstClickPoint.datasetIndex].data[firstClickPoint.index] as TimelineDataPoint;
                if (timelineDataPoint) {
                    switch (timelineDataPoint.type) {
                        case TimelinePointType.Scorecard:
                            this.$emit("scorecard-data-point-clicked", timelineDataPoint.id);
                            break;
                        case TimelinePointType.Incident:
                            this.$emit("incident-data-point-clicked", timelineDataPoint.id);
                            break;
                        case TimelinePointType.SupplierEvent:
                            this.$emit("supplier-event-data-point-clicked", timelineDataPoint.id);
                            break;
                        case TimelinePointType.Section:
                        case TimelinePointType.Question:
                            if (timelineDataPoint.details.scorecardID) {
                                this.$emit("scorecard-data-point-clicked", timelineDataPoint.details.scorecardID);
                            } else {
                                // TODO: For scores not on a scorecard, open ScoreDetails
                            }
                            break;
                    }
                }
            }
        },
        getTimelineData: async function () {
            this.loading = true;
            try {
                const query = {
                    supplierID: this.supplierID,
                    afterDate: this.graphStartDateString ? new Date(this.graphStartDateString).toISOString() : new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString(), // one year ago today
                    beforeDate: this.graphEndDateString ? new Date(this.graphEndDateString).toISOString() : new Date().toISOString(),
                    includeScorecards: this.showScorecards,
                    includeIncidents: this.showIncidents,
                    includeSupplierEvents: this.showSupplierEvents,
                    questionSeriesIDs: Object.keys(this.showQuestions).filter(id => this.showQuestions[id]),
                    sectionSeriesIDs: Object.keys(this.showSections).filter(id => this.showSections[id]),
                };
                this.timelineSeries = await this.$timelineService.getTimelineSeries(query);
                this.updateFiltersInLocalStorage();
                if (this.timelineSeries.length) {
                    this.timelineSeries.forEach(series =>
                        series.points.sort((a: TimelinePoint, b: TimelinePoint) => {
                            if (a.timestampUTC < b.timestampUTC) return -1;
                            if (a.timestampUTC > b.timestampUTC) return 1;
                            return 0;
                        })
                    );

                    this.graphStartDateString = new Date(query.afterDate).toISOString();
                    this.graphEndDateString = new Date(query.beforeDate).toISOString();
                } else {
                    this.graphStartDateString = new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString(); // one year ago today
                    this.graphEndDateString = new Date().toISOString();
                }
            } catch {
                this.errorMessage = "Failed to fetch supplier timeline data";
                this.showError = true;
            } finally {
                this.loading = false;
            }
        },
        async getScorecardTemplate() {
            this.loadingScorecardTemplate = true;
            try {
                this.scorecardTemplate = await this.$scoreService.getScorecardTemplate();
                this.scorecardTemplate.sectionSeries.sort((a, b) => a.order - b.order);
                this.scorecardTemplate.sectionSeries.forEach(s => s.questionSeries.sort((a, b) => a.order - b.order));
                this.tabList = ["Overall Settings", ...this.scorecardTemplate.sectionSeries.map(s => s.title)];
                this.scorecardTemplate.sectionSeries.forEach(s => {
                    if (!(s.id in this.showSections)) {
                        this.showSections[s.id] = false;
                    }
                    s.questionSeries.forEach(q => {
                        if (!(q.id in this.showQuestions)) {
                            this.showQuestions[q.id] = false;
                        }
                    });
                });

                // restore any filters from before we reset everything to false
                this.updateFiltersFromLocalStorage();
            } catch {
                this.errorMessage = "Failed to fetch scorecard template";
                this.showError = true;
            } finally {
                this.loadingScorecardTemplate = false;
            }
        },
        onGraphStartDateChanged() {
            this.graphStartDateMenu = false;
            this.getTimelineData();
        },
        onGraphEndDateChanged() {
            this.graphEndDateMenu = false;
            this.getTimelineData();
        },
        formatDatepickerDateForDisplay: function (dateString: string) {
            if (!dateString) return "";
            const date = new Date(dateString);
            return formatDatepickerDate(date);
        },
        formatDateWithTimestampForDisplay: function (dateString: string) {
            const date = new Date(dateString);
            return formatDateWithTimestamp(date);
        },
        unpublished(ctx: any, value: number[]): number[] | undefined {
            return ctx.p1.raw.published ? undefined : value;
        },
        getDatasetForSeries(series: TimelineSeries): ChartDataset | null {
            if (series == undefined) return null;
            return {
                label: series.descriptor,
                borderColor: this.getColorForSeries(series.descriptor), // Line color
                pointBorderColor: this.getColorForSeries(series.descriptor),
                pointRadius: this.pointRadius,
                hoverRadius: 7,
                pointHitRadius: 3,
                pointBorderWidth: this.pointBorderWidth,
                segment: {
                    borderDash: ctx => this.unpublished(ctx, [6, 6]), // the array the controls length and spacing of the dashes
                },
                showLine: this.showLine(series.descriptor),
                pointStyle: this.getPointStyle(series.descriptor),
                data: series.points.map(p => {
                    return {
                        x: new Date(p.timestampUTC).getTime(),
                        y: p.value ? Math.round(p.value * 100) : 0, // Score value as a %
                        id: p.sourceID,
                        type: p.type,
                        published: p.published,
                        details: p.details,
                    } as TimelineDataPoint;
                }),
            };
        },
        showLine(seriesDescriptor: string): boolean {
            return !["Incidents", "Events"].includes(seriesDescriptor);
        },
        getQuestionSeriesIDsForSection(sectionID: string): string[] {
            return this.scorecardTemplate!.sectionSeries.find(s => s.id == sectionID)!.questionSeries.map(q => q.id);
        },
        showAllSeriesInSection(sectionID: string): void {
            this.showSections[sectionID] = true;
            const questionSeriesIDs = this.getQuestionSeriesIDsForSection(sectionID);
            questionSeriesIDs!.forEach(id => (this.showQuestions[id] = true));
            this.getTimelineData();
        },
        hideAllSeriesInSection(sectionID: string): void {
            this.showSections[sectionID] = false;
            const questionSeriesIDs = this.getQuestionSeriesIDsForSection(sectionID);
            questionSeriesIDs!.forEach(id => (this.showQuestions[id] = false));
            this.getTimelineData();
        },
        displayingAllSeriesInSection(sectionID: string): boolean {
            const questionSeriesIDs = this.getQuestionSeriesIDsForSection(sectionID);
            return this.showSections[sectionID] && questionSeriesIDs.every(id => this.showQuestions[id]);
        },
        showAllOverall(): void {
            this.showScorecards = true;
            this.showIncidents = true;
            this.showSupplierEvents = true;
            this.getTimelineData();
        },
        hideAllOverall(): void {
            this.showScorecards = false;
            this.showIncidents = false;
            this.showSupplierEvents = false;
            this.getTimelineData();
        },
        updateFiltersInLocalStorage(): void {
            localStorage.setItem("showScorecards", this.showScorecards.toString());
            localStorage.setItem("showIncidents", this.showIncidents.toString());
            localStorage.setItem("showSupplierEvents", this.showSupplierEvents.toString());
            localStorage.setItem("showSections", JSON.stringify(this.showSections));
            localStorage.setItem("showQuestions", JSON.stringify(this.showQuestions));
            localStorage.setItem("afterDate", this.graphStartDateString);
            localStorage.setItem("beforeDate", this.graphEndDateString);
        },
        updateFiltersFromLocalStorage(): void {
            if (localStorage.getItem("showScorecards")) {
                this.showScorecards = localStorage.getItem("showScorecards") === "true";
            }
            this.showIncidents = localStorage.getItem("showIncidents") === "true";
            this.showSupplierEvents = localStorage.getItem("showSupplierEvents") === "true";
            if (localStorage.getItem("showSections")) {
                this.showSections = JSON.parse(localStorage.getItem("showSections")!);
            }
            if (localStorage.getItem("showQuestions")) {
                this.showQuestions = JSON.parse(localStorage.getItem("showQuestions")!);
            }
            if (localStorage.getItem("afterDate")) {
                this.graphStartDateString = localStorage.getItem("afterDate")!;
            }
            if (localStorage.getItem("beforeDate")) {
                this.graphEndDateString = localStorage.getItem("beforeDate")!;
            }
        },
        displayingAllOverall(): boolean {
            return this.showScorecards && this.showIncidents && this.showSupplierEvents;
        },
        getPointStyle(seriesDescriptor: string): string {
            return ["Incidents", "Events"].includes(seriesDescriptor) ? "rectRot" : "circle";
        },
        getColorForSeries(seriesDescriptor: string): string {
            const sectionColors = [
                "#198C19", // green
                "#292963", // purple
                "#871445", // pink
                "#056E5A", // teal
                "#B50A0A", // orange
            ];
            const questionColors = [
                [
                    // greens
                    "#B4F0AF",
                    "#8CDC8C",
                    "#64CD6E",
                    "#3CBE46",
                    "#32A532",
                    "#1E961E",
                ],
                [
                    // purples
                    "#C2B0E5",
                    "#A687D6",
                    "#875EC7",
                    "#6938BF",
                    "#4F3D82",
                    "#3D3378",
                ],
                [
                    // pinks
                    "#FAABF5",
                    "#F58CEB",
                    "#D659BF",
                    "#CC248C",
                    "#AB146E",
                    "#A11463",
                ],
                [
                    // teals
                    "#A5E6DC",
                    "#82DCCD",
                    "#50CDB4",
                    "#00B996",
                    "#00967D",
                    "#00826E",
                ],
                [
                    // oranges
                    "#F5BFB5",
                    "#EB9191",
                    "#EB5E5E",
                    "#F03D2E",
                    "#DB381F",
                    "#D11F1F",
                ],
            ];

            switch (seriesDescriptor) {
                case "Incidents":
                    return "#00D2DC";
                case "Events":
                    return "#6E8CC8";
                case "Scorecards":
                case "Overall Settings":
                    return "#0014DC";
                default: {
                    const sectionTitles = this.scorecardTemplate!.sectionSeries.map(s => s.title);
                    const questionTitles = this.scorecardTemplate!.sectionSeries.map(s => s.questionSeries.map(q => q.title)).flat();
                    if (sectionTitles.includes(seriesDescriptor)) {
                        return sectionColors[this.scorecardTemplate!.sectionSeries.find(s => s.title == seriesDescriptor)!.order];
                    } else if (questionTitles.includes(seriesDescriptor)) {
                        const sectionForThisQuestion = this.scorecardTemplate!.sectionSeries.find(s => s.questionSeries.some(q => q.title == seriesDescriptor));
                        return questionColors[sectionForThisQuestion!.order][sectionForThisQuestion!.questionSeries.find(q => q.title == seriesDescriptor)!.order];
                    } else {
                        return "#AAAAAA";
                    }
                }
            }
        },
    },
    computed: {
        chartOptions(): any {
            let xAxisTimeUnit = "month";
            let xAxisMin = new Date(new Date().setFullYear(new Date().getFullYear() - 1)).getTime(); // one year ago today
            let xAxisMax = new Date().getTime();

            if (this.timelineSeries.length) {
                xAxisMin = this.graphStartDateString ? new Date(this.graphStartDateString).getTime() : new Date(this.timelineSeries[0].points[0].timestampUTC).getTime();
                xAxisMax = this.graphEndDateString
                    ? new Date(this.graphEndDateString).getTime()
                    : new Date(this.timelineSeries[0].points[this.timelineSeries[0].points.length - 1].timestampUTC).getTime();

                // Widen the time scale if the span between the first and last event is broad enough.
                if (this.timelineSeries.length >= 2) {
                    if (xAxisMax - xAxisMin > START_TIME_FOR_YEAR_TIME_SCALE) {
                        xAxisTimeUnit = "year";
                    }
                }
            }

            // Debounce for updating the scale when we change the bounds
            var scaleHasUpdated = false;

            const showingEvents = this.timelineSeries.some(s => s.descriptor == "Events");
            const showingIncidents = this.timelineSeries.some(s => s.descriptor == "Incidents");
            const showingScores = this.timelineSeries.some(s => !["Events", "Incidents"].includes(s.descriptor));

            return {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: {
                        type: "time",
                        time: {
                            unit: xAxisTimeUnit,
                        },
                        min: xAxisMin,
                        max: xAxisMax,
                        // make room for points appearing on the edge of the timeline, so they don't get cut off
                        afterDataLimits: (scale: Scale) => {
                            // If we've already changed the bounds, don't mess with the scale limits; it will break pan and zoom
                            if (scaleHasUpdated) {
                                return;
                            }
                            scaleHasUpdated = true;

                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore Disabling type checking on this line in the absence of a type to cast the ref to.
                            const lineChart = this.$refs.chart! as ChartJS;

                            // Calculate the ratio of milliseconds represented on the graph to pixels of the chart's width
                            const millisecondScalar = (xAxisMax - xAxisMin) / lineChart.width;

                            // Scale the radius of a datapoint in pixels to the number of milliseconds we need to add as padding
                            const xPadding = (this.pointRadius + this.pointBorderWidth) * millisecondScalar;
                            scale.max = xAxisMax + xPadding;
                            scale.min = xAxisMin - xPadding;
                        },
                        afterTickToLabelConversion: (linearScale: LinearScale) => {
                            const ticks = linearScale.ticks;
                            for (const tick of ticks) {
                                if (typeof tick.label == "string") {
                                    tick.label = tick.label.split(" ");
                                }
                            }
                        },
                    },
                    y: {
                        min: showingScores ? 0 : 115,
                        max: 100, // Axis represents a percentage
                        afterDataLimits: (scale: Scale) => {
                            // Adjust the minimum in case we aren't displaying any scores
                            scale.min = showingScores ? 0 : 115;

                            // Adjust the maximum in case we're displaying events or incidents
                            if (showingEvents && showingIncidents) {
                                scale.max = 145;
                            } else if (showingIncidents || showingEvents) {
                                scale.max = 125;
                            } else {
                                // make room for points appearing on the edge of the timeline, so they don't get cut off
                                scale.max = 105;
                            }
                        },
                        afterTickToLabelConversion(axis: LinearScale) {
                            // Filter out unwanted ticks and replace certain numerical tick labels with words, if necessary
                            axis.ticks = axis.ticks
                                // only show quintile gridlines
                                .filter(t => t.value % 20 == 0)
                                .map(t => {
                                    // If we're displaying events and incidents, events go on top
                                    switch (t.value) {
                                        case 140:
                                            t.label = "Events";
                                            return t;
                                        case 120:
                                            t.label = showingIncidents ? "Incidents" : "Events";
                                            return t;
                                        default:
                                            // Don't label the x-axis 100 if we aren't displaying any score data
                                            if (!showingScores && t.label == "100") {
                                                t.label = "";
                                            }
                                            return t;
                                    }
                                });
                        },
                    },
                },
                plugins: {
                    tooltip: {
                        displayColors: false,
                        position: "custom",
                        callbacks: {
                            title: (context: ChartPointContext[]): string => {
                                if (context[0].raw.type == TimelinePointType.Question) return `${context[0].raw.details.sectionTitle}: ${context[0].raw.details.questionTitle}`;
                                return context[0].dataset.label!;
                            },
                            label: (context: ChartPointContext) => {
                                // If we want fully custom tooltips, use https://www.chartjs.org/docs/3.6.0/configuration/tooltip.html#external-custom-tooltips
                                let scoreLine = null;
                                if (![TimelinePointType.Incident, TimelinePointType.SupplierEvent].includes(context.raw.type)) {
                                    const formattedScore = (context.raw.details.percentScore! * 100).toFixed(1);
                                    scoreLine = `Score: ${formattedScore}%`;
                                }

                                let severityLine = null;
                                if (context.raw.type == TimelinePointType.Incident) {
                                    severityLine = `Severity: ${UIIncidentSeverity[context.raw.details.incidentSeverity!]}`;
                                }

                                let lastUpdatedLine: string | null = "Last updated ";
                                if (context.raw.details.updatedTimestampUTC) {
                                    lastUpdatedLine += this.formatDateWithTimestampForDisplay(context.raw.details.updatedTimestampUTC.toString());
                                }
                                if (context.raw.details.updatedBy) {
                                    lastUpdatedLine += ` by ${context.raw.details.updatedBy}`;
                                }
                                if (lastUpdatedLine == "Last updated ") {
                                    lastUpdatedLine = null;
                                }

                                // only return non-null lines
                                return [scoreLine, severityLine, lastUpdatedLine].filter(l => l != null);
                            },
                        },
                    },
                    legend: {
                        display: false,
                    },
                    zoom: {
                        zoom: {
                            wheel: {
                                enabled: true,
                            },
                            mode: "x",
                            onZoomComplete: (chart: any) => {
                                // boxes[2] points to the X axis
                                this.graphStartDateString = new Date(chart.chart.boxes[2].min).toISOString().split("T")[0];
                                this.graphEndDateString = new Date(chart.chart.boxes[2].max).toISOString().split("T")[0];
                                this.getTimelineData();
                            },
                        },
                        pan: {
                            enabled: true,
                            mode: "x",
                            onPanComplete: (chart: any) => {
                                // boxes[2] points to the x axis
                                this.graphStartDateString = new Date(chart.chart.boxes[2].min).toISOString().split("T")[0];
                                this.graphEndDateString = new Date(chart.chart.boxes[2].max).toISOString().split("T")[0];
                                this.getTimelineData();
                            },
                        },
                    },
                },
                onClick: this.chartClicked,
            };
        },
        chartData(): ChartData {
            // Convert scorecard Timeline Events into line data points
            if (!this.timelineSeries.length) return { datasets: [] };
            return {
                datasets: this.timelineSeries.map(s => this.getDatasetForSeries(s)),
            } as ChartData;
        },
        beforeDate(): string | null {
            return this.$route.query.beforeDate ? (this.$route.query.beforeDate as string) : null;
        },
        afterDate(): string | null {
            return this.$route.query.afterDate ? (this.$route.query.afterDate as string) : null;
        },
    },
});
interface TimelineDataPoint {
    x: number;
    y: number;
    id: string;
    type: TimelinePointType;
    published: boolean;
    details: TimelinePointDetails;
}

interface ChartPointContext extends ScriptableContext<typeof LineChartGenerator> {
    raw: TimelineDataPoint;
    parsed: {
        x: number;
        y: number;
    };
    dataset: ChartDataset;
}
